import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Bell, Menu, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { notificationsApi } from '../api/notifications';
import { formatRelativeTime } from '../lib/format';
import { navTopItems } from './navigationConfig';
import { VRI_LOGO_DATA_URI } from '../assets/logo';

function UserAvatar({ name, photo, size = 34 }: { name: string; photo: string | null; size?: number }) {
  if (photo) return <img src={photo} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />;
  const initials = name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--vri-green)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700 }}>
      {initials}
    </div>
  );
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function dashboardGreeting(firstName: string): string {
  return `${greeting()}, ${firstName}`;
}

/**
 * Flat single links only - no dropdowns/mega-menus, per explicit
 * feedback that the header menus were too much. Every module link
 * goes straight to that module's own hub page, where the detailed
 * sub-navigation lives as clickable cards on the page itself (the
 * pattern HR and Finance already used successfully before this
 * redesign even started).
 *
 * useAuth() is called exactly once, at the top level - every
 * permission check below reads from that same Set via a plain
 * function call, never a hook called inside a loop or .filter(),
 * which is a genuine Rules-of-Hooks violation already found and
 * fixed twice this session (once on the Project Workspace page,
 * once on this very file's first draft).
 */
export function TopNav() {
  const { user, permissions } = useAuth();
  const hasPermission = (p: string) => permissions.has(p);

  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const queryClient = useQueryClient();

  const notificationsQuery = useQuery({ queryKey: ['notifications-mine'], queryFn: () => notificationsApi.mine(), refetchInterval: 60_000 });
  const markReadMutation = useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications-mine'] }),
  });
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setProfileOpen(false); setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Closes the drawer automatically whenever the route changes, so
  // tapping a link doesn't leave a stale open drawer behind on the
  // next page.
  const location = useLocation();
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const visibleTopItems = navTopItems.filter((item) => item.requiredPermission === null || hasPermission(item.requiredPermission));

  return (
    <header className="app-topnav" ref={navRef}>
      <button className="app-topnav-hamburger" onClick={() => setDrawerOpen((v) => !v)} aria-label="Menu">
        {drawerOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      <div className="app-topnav-brand">
        <img src={VRI_LOGO_DATA_URI} alt="Verdantrise" style={{ height: 32, width: 'auto' }} />
        <div className="app-topnav-brand-text">
          <span className="app-topnav-brand-name">VConnect</span>
          <span className="app-topnav-brand-tag">People | Projects | Better Lives</span>
        </div>
      </div>

      <nav className="app-topnav-main">
        {visibleTopItems.map((item) => (
          <NavLink key={item.label} to={item.to} end={item.to === '/'} className={({ isActive }) => 'app-topnav-link' + (isActive ? ' app-topnav-link-active' : '')}>
            {item.label}
          </NavLink>
        ))}
      </nav>

      {drawerOpen && (
        <>
          <div className="app-topnav-drawer-overlay" onClick={() => setDrawerOpen(false)} />
          <nav className="app-topnav-drawer">
            {visibleTopItems.map((item) => (
              <NavLink key={item.label} to={item.to} end={item.to === '/'} className={({ isActive }) => 'app-topnav-drawer-link' + (isActive ? ' app-topnav-drawer-link-active' : '')}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </>
      )}

      <div className="app-topnav-right">
        <div className="notification-wrapper">
          <button className="icon-button" onClick={() => setNotifOpen((v) => !v)} aria-label="Notifications">
            <Bell size={20} />
            {unreadCount > 0 && <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
          </button>
          {notifOpen && (
            <div className="notification-dropdown">
              <div className="notification-dropdown-header">Notifications</div>
              {notificationsQuery.data && notificationsQuery.data.data.length === 0 && <div className="notification-empty">You're all caught up.</div>}
              {notificationsQuery.data?.data.map((n) => (
                <button key={n.id} className="notification-item" onClick={() => markReadMutation.mutate(n.id)}>
                  <div className="notification-item-title">{n.title}</div>
                  <div className="notification-item-message">{n.message}</div>
                  <div className="notification-item-time">{formatRelativeTime(n.created_at)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {user && (
          <div className="app-topnav-profile-wrap">
            <button className="app-topnav-profile-trigger" onClick={() => setProfileOpen((v) => !v)}>
              <UserAvatar name={user.fullName} photo={user.profilePhotoData} />
              <div className="app-topnav-profile-text">
                <span className="app-topnav-profile-name">{user.fullName}</span>
                {user.jobTitle && <span className="app-topnav-profile-title">{user.jobTitle}</span>}
              </div>
              <ChevronDown size={14} />
            </button>
            {profileOpen && (
              <div className="app-topnav-profile-menu">
                {hasPermission('employees.view_own') && <button onClick={() => { navigate('/staff'); setProfileOpen(false); }}>My Profile</button>}
                <a href="/auth/logout">Sign Out</a>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
